import * as moment from "moment";
import { ModelAssumptionsUtil } from "src/app/core/v2/state/valuation-process/core/income/model-assumptions-util";
import { CostAssessmentRecordRow, DepreciatedRecordRow, LandInput } from "../../types";
import { diminishingBalanceMethod, DiminishingBalanceOptions } from "./diminishing-balance";
import { gompertzMethod, GompertzOptions } from "./gompertz";
import { mixedMethod, MixedOptions } from "./mixed";
import { normalizeRecords } from "./normalize";
import { physicalObsolescenceMethod, PhysicalObsolescenceOptions } from "./physical-obsolescence";
import { richardsCurveMethod, RichardsCurveOptions } from "./richards-curve";
import { sCurveMethod, SCurveOptions } from "./s-curve-method";
import { straightLineMethod, StraightLineOptions } from "./straight-line-method";
import { threeWaysAddUpMethod, ThreeWaysAddUpOptions } from "./three-ways-add-up";
import { threeWaysMultiMethod, ThreeWyasMultiOptions } from "./three-ways-multi";
import { recordsHasNullDepreciatedCost, totalCostOfRecords, totalDepreciatedCostOfRecords } from "./utils";
import { weibullCDFMethod, WeibullCDFOptions } from "./weibull-cdf";


export type DepreciationOptions 
  = SCurveOptions
  | StraightLineOptions
  | DiminishingBalanceOptions
  | PhysicalObsolescenceOptions
  | ThreeWaysAddUpOptions
  | ThreeWyasMultiOptions
  | RichardsCurveOptions
  | WeibullCDFOptions
  | GompertzOptions
  | MixedOptions

export interface RecordOptions {
  valuationDate: Date,
  buildingConstructionDate: Date,
  useLandValueInput: boolean
  useSizeAsQuantity: boolean,
  landInput: LandInput,
  size: number,
  unitAreaMeasurementId: number
  level: number
}

export function depreciation(
  records: CostAssessmentRecordRow[],
  recordOptions: RecordOptions,
  options: DepreciationOptions
) {
  if (options === null) return null;
  if (options.kind === 's-curve') {
    return sCurveMethod(records, recordOptions, options)
  }
  if (options.kind === 'straight-line') {
    return straightLineMethod(records, recordOptions, options)
  }
  if (options.kind === 'diminishing-balance') {
    return diminishingBalanceMethod(records, recordOptions, options)
  }
  if (options.kind === 'mixed') {
    return mixedMethod(records, recordOptions, options)
  }
  if (options.kind === 'physical-obsolescence') {
    return physicalObsolescenceMethod(records, recordOptions, options)
  }
  if (options.kind === 'three-ways-add-up') {
    return threeWaysAddUpMethod(records, recordOptions, options)
  }
  if (options.kind === 'three-ways-multi') {
    return threeWaysMultiMethod(records, recordOptions, options)
  }
  if (options.kind === 'richards-curve') {
    return richardsCurveMethod(records, recordOptions, options)
  }
  if (options.kind === 'weibull-cdf') {
    return weibullCDFMethod(records, recordOptions, options)
  }
  if (options.kind === 'gompertz') {
    return gompertzMethod(records, recordOptions, options)
  }
  return null
}

export function getDepreciationOptions(
  methodId: number,
  sCurveSteepness: number,
  economicLife: number,
  salvageValue: number,
  depreciationFactorPerYear: number,
  levelId: number,
  from: string,
  physicalDepreciationFactor: number,
  functionalDepreciation: number,
  economicDepreciation: number,
  richardsSteepness: number,
  richardsInflection: number,
  richardsShape: number,
  weibullShape: number,
  gompertzA: number,
  gompertzB: number
): DepreciationOptions {
  if (methodId === 1) {
    const options: PhysicalObsolescenceOptions = {
      kind: 'physical-obsolescence',
      physicalDepreciationFactor
    }
    return options
  }
  if (methodId === 2) {
    const options: SCurveOptions = {
      kind: 's-curve',
      steepness: sCurveSteepness,
      economicLife
    }
    return options
  }
  if (methodId === 3) {
    const options: ThreeWaysAddUpOptions = {
      kind: 'three-ways-add-up',
      physicalDepreciationFactor,
      functionalDepreciation,
      economicDepreciation
    }
    return options
  }
  if (methodId === 4) {
    const options: ThreeWyasMultiOptions = {
      kind: 'three-ways-multi',
      physicalDepreciationFactor,
      functionalDepreciation,
      economicDepreciation
    }
    return options
  }
  if (methodId === 5) {
    const options: DiminishingBalanceOptions = {
      kind: 'diminishing-balance',
      economicLife,
      depreciationFactorPerYear
    }
    return options
  }
  if (methodId === 6) {
    const options: StraightLineOptions = {
      kind: 'straight-line',
      salvageValue: salvageValue,
      economicLife
    }
    return options
  }
  if (methodId === 7) {
    const options: RichardsCurveOptions = {
      kind: 'richards-curve',
      richardsSteepness,
      richardsInflection,
      richardsShape,
      economicLife 
    }
    return options
  }
  if (methodId === 8) {
    const options: WeibullCDFOptions = {
      kind: 'weibull-cdf',
      economicLife,
      weibullShape
    }
    return options
  }
  if (methodId === 9) {
    const options: GompertzOptions = {
      kind: 'gompertz',
      economicLife,
      gompertzA,
      gompertzB
    }
    return options
  }
  if (methodId === 10) {
    const options: MixedOptions = {
      kind: 'mixed',
      levelId: levelId,
      from
    }
    return options
  }
  return null
}